import { BoxWrapper, ConvertStringToReact, InputField, InlineErrorView } from '@common/components'
import { useClearField } from '@common/hooks'
import { connectRedux, formatCurrency, GoToComponentProps } from '@common/utils'
import { CHECKOUTFORM_FIELDS, CHECKOUTFORM_STEPS } from '@buy/constants'
import {
    BuyAccountFormInfoActionsType,
    mapBuyAccountFormInfoActions,
    mapBuyAccountFormInfoState,
} from '@buy/connectedComponents/AccountFormInfo/connector'
import { BuyAccountFormInfoState } from '@buy/connectedComponents/AccountFormInfo/state'
import { BuyFormData } from '@buy/state/buy-form/state'
import { manuallyValidateFieldData } from '@buy/utils'
import { Field, FieldProps, FormikProps } from 'formik'
import React, { FunctionComponent } from 'react'
import { BuyPasswordValidation } from '@buy/components/PasswordValidation'
import passwordValidationStyles from '@buy/components/PasswordValidation/password-validation.module.css'
import CheckboxField from '@common/components/CheckboxField'
import { BillingPeriod, COUNTRIES } from '@common/constants'
import { useSearchParams } from 'react-router-dom'
import { showDisclaimer } from '@common/constants/search-query-params'
import { flushSync } from 'react-dom'
import { useSelector } from 'react-redux'
import { getProductSettingsData } from '@buy/state/selectors'
import accountStyles from './account-info.module.css'
import { CountryCodeInfo } from '../CheckoutFormContainer'

export interface BuyAccountFormInfoComponentProps {
    useSameForBilling: boolean
    resetFatalError: () => void
    canValidateAddress: (values: BuyFormData) => void
    formikRef: React.RefObject<FormikProps<BuyFormData>>
    setCheckoutFormInlineError: (value: string) => void
    removeCheckoutFormInlineError: (value: string) => void
    inlineErrors: { [key: string]: string } | Record<string, unknown>
    isSubmittingRef: React.RefObject<boolean>
    setOnInitialValue: React.Dispatch<React.SetStateAction<Record<string, string>>>
    isUserLoggedIn: boolean
    countryCodeInfo: CountryCodeInfo
}

type BuyAccountFormInfoProps = GoToComponentProps<
    BuyAccountFormInfoState,
    BuyAccountFormInfoActionsType,
    BuyAccountFormInfoComponentProps
>
export const BuyAccountFormInfo: FunctionComponent<BuyAccountFormInfoProps> = ({ state, actions, props }) => {
    const {
        useSameForBilling,
        resetFatalError,
        canValidateAddress,
        formikRef,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        inlineErrors,
        isSubmittingRef,
        setOnInitialValue,
        isUserLoggedIn,
        countryCodeInfo,
    } = props
    const { content, errorContent, clearEmail, steps, finalTotal, calculatedTax, billingTenure, currency, locale } =
        state
    const createUserAccountStep = steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]
    const userAccountCreated = createUserAccountStep.completed && !createUserAccountStep.error
    const [searchParams] = useSearchParams()
    const { currencySettings } = useSelector(getProductSettingsData)
    const userLogIn = () => actions.userLogin('billing view', window.location.pathname)
    const existingUserCheck = (email: string, form: BuyFormData) => {
        actions.checkExistingUser(email, form)
    }

    const getRenewalDate = () => {
        const today = new Date()
        const day = today.getDate()
        const month = today.toLocaleString('default', { month: 'long' })
        const year = today.getFullYear() + 1
        return `${month} ${day}, ${year}`
    }

    // clear email
    useClearField(clearEmail, formikRef, CHECKOUTFORM_FIELDS.EMAIL)

    const renderDisclaimer = () => {
        return (
            <>
                <div className={accountStyles['disclaimer-label']}>
                    <ConvertStringToReact
                        htmlString={content.clicktoCancelDisclaimer
                            .replace(
                                '{amount}',
                                `${formatCurrency(
                                    finalTotal - calculatedTax,
                                    locale,
                                    currency,
                                    currencySettings,
                                    true
                                )}`
                            )
                            .replace(
                                '{term}',
                                billingTenure.selected === BillingPeriod.ANNUAL ? content.year : content.month
                            )
                            .replace('{renewalDate}', getRenewalDate())}
                    />
                </div>

                <div className={accountStyles['disclaimer-checkbox-wrapper']}>
                    <Field type="checkbox" name={CHECKOUTFORM_FIELDS.IS_DISCLAIMER_AGREED}>
                        {({ form, field, meta }: FieldProps) => {
                            return (
                                <InlineErrorView
                                    fieldData={{ form, field, meta }}
                                    inlineErrors={inlineErrors}
                                    setErrorCall={setCheckoutFormInlineError}
                                    removeErrorCall={removeCheckoutFormInlineError}
                                >
                                    <CheckboxField
                                        id="disclaimer-checkbox"
                                        label={content.clicktoCancelCheckbox}
                                        modifier={accountStyles['disclaimer-checkbox']}
                                        name={field.name}
                                        onChange={async (e) => {
                                            flushSync(() => {
                                                // Use flushSync to avoid issues with React 18's batching behavior
                                                field.onChange(e)
                                            })
                                            await form.setFieldValue(
                                                CHECKOUTFORM_FIELDS.IS_DISCLAIMER_AGREED,
                                                e.target.checked
                                            )
                                            sessionStorage.setItem(field.name, e.target.checked.toString())
                                            form.validateField(field.name)
                                        }}
                                        value={field.checked}
                                        qaTag="checkout-form-disclaimer"
                                        error={meta.error}
                                    />
                                </InlineErrorView>
                            )
                        }}
                    </Field>
                </div>
            </>
        )
    }

    if (isUserLoggedIn)
        if (countryCodeInfo.current === COUNTRIES.US && searchParams.get(showDisclaimer) === 'true')
            return (
                <BoxWrapper>
                    <div className="account-info">{renderDisclaimer()}</div>
                </BoxWrapper>
            )
        else return <></>
    return (
        <BoxWrapper>
            <div className="account-info">
                {!isUserLoggedIn && (
                    <>
                        <h3>
                            <ConvertStringToReact htmlString={content.createAnAccount} />{' '}
                            <button
                                className={accountStyles.signin}
                                onClick={userLogIn}
                                type="button"
                                data-qat="login-account-button"
                            >
                                {content.signIn}
                            </button>
                        </h3>
                        <div className={accountStyles.grid}>
                            <Field name={CHECKOUTFORM_FIELDS.EMAIL}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                autoFocus
                                                error={meta.error}
                                                id="login__email"
                                                label={content.email}
                                                name={field.name}
                                                value={field.value}
                                                type="email"
                                                wrapperClass={accountStyles.email}
                                                onFocus={(e) => {
                                                    if (clearEmail) actions.setEmailClear(false)
                                                    setOnInitialValue((oldState) => ({
                                                        ...oldState,
                                                        [field.name]: e.target.value,
                                                    }))
                                                }}
                                                onBlur={async (e) => {
                                                    await form.setFieldValue(field.name, e.target.value.trim())
                                                    field.onBlur(e)
                                                    form.validateField(field.name)
                                                    if (!meta.error && e.target.value.length > 0) {
                                                        existingUserCheck(e.target.value, form.values)
                                                        canValidateAddress(form.values)
                                                    }

                                                    resetFatalError()
                                                }}
                                                onChange={(e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    form.validateField(field.name)
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                }}
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                disabled={userAccountCreated}
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>
                            <Field name={CHECKOUTFORM_FIELDS.FIRST_NAME}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                name={field.name}
                                                value={field.value}
                                                error={meta.error}
                                                id="first-name"
                                                label={content.firstName}
                                                maxLength={50}
                                                type="text"
                                                wrapperClass={accountStyles['first-name']}
                                                onBlur={async (e) => {
                                                    await form.setFieldValue(field.name, e.target.value.trim())
                                                    field.onBlur(e)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                onChange={async (e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    await form.setFieldValue(field.name, e.target.value.trim())
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                    if (useSameForBilling) {
                                                        sessionStorage.setItem(
                                                            CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME,
                                                            e.target.value.trim()
                                                        )
                                                        await form.setFieldValue(
                                                            CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME,
                                                            e.target.value
                                                        )
                                                    }
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                onFocus={(e) => {
                                                    setOnInitialValue((oldState) => ({
                                                        ...oldState,
                                                        [field.name]: e.target.value,
                                                    }))
                                                }}
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                disabled={userAccountCreated}
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>

                            <Field name={CHECKOUTFORM_FIELDS.LAST_NAME}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                name={field.name}
                                                value={field.value}
                                                error={meta.error}
                                                id="last-name"
                                                label={content.lastName}
                                                type="text"
                                                wrapperClass={accountStyles['last-name']}
                                                onBlur={async (e) => {
                                                    await form.setFieldValue(field.name, e.target.value.trim())
                                                    field.onBlur(e)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                onChange={async (e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    await form.setFieldValue(field.name, e.target.value.trim())
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                    if (useSameForBilling) {
                                                        sessionStorage.setItem(
                                                            CHECKOUTFORM_FIELDS.BILLING_LAST_NAME,
                                                            e.target.value.trim()
                                                        )
                                                        await form.setFieldValue(
                                                            CHECKOUTFORM_FIELDS.BILLING_LAST_NAME,
                                                            e.target.value
                                                        )
                                                    }
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                onFocus={(e) => {
                                                    setOnInitialValue((oldState) => ({
                                                        ...oldState,
                                                        [field.name]: e.target.value,
                                                    }))
                                                }}
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                disabled={userAccountCreated}
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>

                            <Field name={CHECKOUTFORM_FIELDS.PASSWORD}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <BuyPasswordValidation
                                                id="login__password"
                                                label={content.password}
                                                name={field.name}
                                                error={meta.error}
                                                genericPasswordLabel={
                                                    field.value.length > 0
                                                        ? errorContent.errorInvalidPassword
                                                        : errorContent.errorRequiredPassword
                                                }
                                                onBlur={(e) => {
                                                    field.onBlur(e)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                onChange={(e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                }}
                                                value={field.value}
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                touched={meta.touched}
                                                disabled={userAccountCreated}
                                                modifier={passwordValidationStyles['wrapper--compact']}
                                                passwordStrength={errorContent.passwordStrength}
                                                passwordStrengthLevel1={errorContent.passwordStrengthLevel1}
                                                passwordStrengthLevel2={errorContent.passwordStrengthLevel2}
                                                passwordStrengthLevel3={errorContent.passwordStrengthLevel3}
                                                passwordStrengthLevel4={errorContent.passwordStrengthLevel4}
                                                passwordStrengthTipDrawer={errorContent.passwordStrengthTipDrawer}
                                                passwordStrengthTip1={errorContent.passwordStrengthTip1}
                                                passwordStrengthTip2={errorContent.passwordStrengthTip2}
                                                passwordStrengthTip3={errorContent.passwordStrengthTip3}
                                                isSubmittingRef={isSubmittingRef}
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>
                        </div>
                    </>
                )}
                {countryCodeInfo.current === COUNTRIES.US &&
                    searchParams.get(showDisclaimer) === 'true' &&
                    renderDisclaimer()}
            </div>
        </BoxWrapper>
    )
}

export const ConnectedBuyAccountFormInfo = connectRedux(
    BuyAccountFormInfo,
    mapBuyAccountFormInfoState,
    mapBuyAccountFormInfoActions
)
